import { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { UrlObject } from 'url';
import { Drawer, Flex, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerFooter, DrawerOverlay, Text, Box, BoxProps, useBreakpointValue, theme } from '@chakra-ui/react';
import { Size, GenericIndexedObject } from '../../types';
import ArrowLeft from '../Icons/ArrowLeft';
declare type Url = UrlObject | string;
export const CbDrawerFooter: FC<PropsWithChildren> = ({
  children,
  ...props
}) => {
  return <DrawerFooter justifyContent="flex-end" {...props} data-sentry-element="DrawerFooter" data-sentry-component="CbDrawerFooter" data-sentry-source-file="CbDrawer.tsx">
      {children}
    </DrawerFooter>;
};
export interface CbDrawerCloseButtonProps extends BoxProps {
  isMobile?: boolean;
}
export const CbDrawerCloseButton: FC<CbDrawerCloseButtonProps> = ({
  isMobile,
  ...props
}) => {
  if (isMobile) {
    return <Box borderRadius={50} borderColor="cloud.400" borderWidth="1px" height="38px" width="38px" {...props}>
        <DrawerCloseButton position="relative" top="0" left="0" height="36px" width="36px" zIndex={theme.zIndices.docked} />
      </Box>;
  }
  return <Box {...props} data-sentry-element="Box" data-sentry-component="CbDrawerCloseButton" data-sentry-source-file="CbDrawer.tsx">
      <DrawerCloseButton size="lg" width="auto" padding=".5rem 1.25rem" fontWeight="bold" borderWidth="1px" borderRadius={100} borderColor="cloud.400" position="relative" top={0} right={0} _active={{
      background: 'transparent'
    }} _focus={{
      background: 'transparent'
    }} data-sentry-element="DrawerCloseButton" data-sentry-source-file="CbDrawer.tsx">
        {'Close'}
      </DrawerCloseButton>
    </Box>;
};
export interface DrawerConfigParams extends GenericIndexedObject<string | number> {
  drawer: string;
}
export interface DrawerConfig {
  params: DrawerConfigParams;
  returnRoute?: Url;
}
export interface SuperDrawerConfig {
  title: string;
  returnRoute: Url;
}
export interface CbDrawerProps {
  children?: ReactNode;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  config?: DrawerConfig;
  customHeader?: ReactNode;
  drawerSize?: string;
  id?: number | string;
  isMobile?: boolean;
  isOpen?: boolean;
  superDrawerConfig?: SuperDrawerConfig;
  title?: string;
  onOpen?: () => void;
  onClose?: () => void;
  postClose?: () => void;
}
const OVERLAY_BACKGROUND: string = 'linear-gradient(270deg, rgba(5, 37, 91, 0.35) 51.15%, rgba(5, 37, 91, 0.256147) 89.3%, rgba(5, 37, 91, 0) 100%)';
const checkOpen = (params: DrawerConfigParams, query: ParsedUrlQuery): boolean => {
  if (!query) return false;
  return Object.entries(params).reduce((acc, [key, value]) => acc && query[key] === `${value as string}`, true);
};
export const CbDrawer: FC<CbDrawerProps> = ({
  title,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  config,
  customHeader,
  isMobile,
  isOpen,
  onOpen,
  onClose,
  postClose,
  superDrawerConfig,
  drawerSize,
  children
}) => {
  const {
    params,
    returnRoute
  } = config || {};
  const router = useRouter();
  const defaultClose = async (): Promise<void> => {
    if (returnRoute) {
      await router.replace(returnRoute);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('drawer');
      await router.replace(url.href, url.href, {
        shallow: true
      });
    }
    postClose?.();
  };
  const handleClose = async (): Promise<void> => {
    onClose?.();
    postClose?.();
  };
  const shouldDisplayOnMobile = useBreakpointValue({
    base: true,
    md: false
  }, {
    ssr: false
  });
  const open = isOpen || params && checkOpen(params, router?.query) || false;
  useEffect(() => {
    if (open) onOpen?.();
  }, [open]);
  return <>
      <Drawer closeOnEsc={closeOnEsc} closeOnOverlayClick={closeOnOverlayClick} trapFocus={false} onClose={onClose ? handleClose : defaultClose} isOpen={open} size={drawerSize || 'xl'} isFullHeight={false} data-sentry-element="Drawer" data-sentry-source-file="CbDrawer.tsx">
        <DrawerOverlay background={OVERLAY_BACKGROUND} data-sentry-element="DrawerOverlay" data-sentry-source-file="CbDrawer.tsx" />
        <DrawerContent boxShadow="0px 3px 15px 2px rgba(0, 0, 0, 0.0900136)" paddingTop={0} paddingBottom={8} paddingX={0} overflowY="scroll" overflowX="scroll" data-sentry-element="DrawerContent" data-sentry-source-file="CbDrawer.tsx">
          <DrawerHeader display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" marginTop={4} paddingX={0} data-sentry-element="DrawerHeader" data-sentry-source-file="CbDrawer.tsx">
            {customHeader ? customHeader : <Flex paddingRight={{
            base: 3,
            md: drawerSize === 'md' || drawerSize === 'lg' ? 10 : 40
          }} paddingLeft={{
            base: 3,
            md: 12
          }} justifyContent={title ? 'space-between' : 'flex-end'} width="100%" marginTop={shouldDisplayOnMobile ? Size.MobileHeight : Size.DesktopHeight}>
                {title && <Text color="text.primaryDark" fontFamily="codenext" fontWeight="bold" fontSize="2xl">
                    {title}
                  </Text>}
                <CbDrawerCloseButton isMobile={isMobile} />
              </Flex>}
            {superDrawerConfig && <Flex paddingRight={{
            base: 3,
            md: drawerSize === 'md' || drawerSize === 'lg' ? 10 : 40
          }} paddingLeft={{
            base: 3,
            md: 12
          }} width="100%" marginTop={4} cursor="pointer" alignItems="center" justifyContent="flex-start" onClick={async () => {
            await router.replace(superDrawerConfig.returnRoute);
          }}>
                <ArrowLeft width="16px" height="16px" marginRight={2} />
                <Text fontSize="18px" fontWeight="bold">
                  {superDrawerConfig.title}
                </Text>
              </Flex>}
          </DrawerHeader>
          <DrawerBody paddingRight={{
          base: 3,
          md: drawerSize === 'md' || drawerSize === 'lg' ? 10 : 40
        }} paddingLeft={{
          base: 3,
          md: 12
        }} data-sentry-element="DrawerBody" data-sentry-source-file="CbDrawer.tsx">
            {children}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>;
};